import { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Drawer } from '@heroui/react';
import { Tooltip } from '@/components/Tooltip';
import { CloseIcon } from '@/components/icons';
import { installWorkspaceWindowBridge } from '@/lib/workspaceWindow';
import { CompactNavigation } from './CompactNavigation';
import { HeroTopBar } from './HeroTopBar';
import { railInDrawer as isRailInDrawer } from './mobileRail';
import { WorkspacePanelsHost } from './WorkspacePanelsHost';
import { SettingsHost } from './SettingsHost';
import { HeroSidebar } from './sidebar/HeroSidebar';
import { GroupDmModal } from '@/components/channelDrawer/GroupDmModal';
import { HeroChannelHeader } from './header/HeroChannelHeader';
import { ContentPane } from '@/components/ContentPane';
import { HeroMemberPanel } from './members/HeroMemberPanel';
import { QuickSwitcher } from '@/components/QuickSwitcher';
import { ForwardModal } from '@/components/ForwardModal';
import { ReportModal } from '@/components/ReportModal';
import { RemindMenu } from '@/components/RemindMenu';
import { RemindersModal } from '@/components/RemindersModal';
import { DmCallModal } from '@/components/DmCallModal';
import { BroadcastConsentModal } from '@/components/BroadcastConsentModal';
import { BroadcastConsentBanner } from '@/components/BroadcastConsentBanner';
import { DmCallView } from '@/components/DmCallView';
import { MentionProfileHost } from '@/components/userCard/MentionProfileHost';
import { ThreadPanel } from '@/components/ThreadPanel';
import { ImageLightbox } from '@/components/ImageLightbox';
import { initStatusTimer } from '@/lib/statusTimer';
import { initAppearance } from '@/lib/appearancePrefs';
import { ShortcutsOverlay } from '@/components/ui/ShortcutsOverlay';
import { platformOwnsIdentity } from '@/lib/platformIdentity';
import { MentionJumpHotkey } from '@/components/ui/MentionJumpHotkey';
import { VoiceAudioLayer } from '@/components/VoiceAudioLayer';
import { useDataStore } from '@/stores/dataStore';
import { useRailPlacement } from '@/lib/railPlacement';
import { useAppViewport } from '@/lib/useAppViewport';
import { useCoarsePointer } from '@/lib/useCoarsePointer';
import { useIsNarrowViewport } from '@/lib/useIsNarrowViewport';
import { useMessageDeepLink } from '@/lib/useMessageDeepLink';
import { ResizeHandle } from './ResizeHandle';
import {
  getUiPrefs,
  subscribeUiPrefs,
  updateUiPrefs,
  clampSidebarWidth,
  clampMembersWidth,
  isChannelFocusMode,
  setChannelFocusMode,
} from '@/lib/uiPrefs';
import { api } from '@/lib/api';
import { clearEmojis, setGuildEmojis } from '@/lib/emojiRegistry';
import { emitAppEvent, onAppEvent } from '@/lib/appEvents';

/**
 * HeroApp — the HeroUI-native application shell, rendered instead of
 * {@link MainLayout} when skin = heroui. Built from scratch on HeroUI:
 * its own server rail (HeroTopBar) + a clean HeroUI surface layout. Shares
 * ALL business logic with the Aether shell (same dataStore, sockets, voice,
 * effects) — only the presentation differs. Functional regions (channel
 * drawer, content pane, member panel, modals, voice layer) are reused while
 * each is progressively rebuilt on HeroUI; the Aether MainLayout is never
 * touched, so the old skin stays pixel-identical and switchable.
 */
export function HeroApp() {
  const { t } = useTranslation();
  const embedded = platformOwnsIdentity();
  useEffect(() => installWorkspaceWindowBridge(), []);
  // Server-rail placement (top | left) — user choice.
  //
  // The left rail needs a desktop-width canvas. On phones and tablets it takes
  // room away from the chat; at 768px the DM home card was clipped inside the
  // room. Keep the saved choice and show the top rail below the lg breakpoint.
  const narrowViewport = useIsNarrowViewport();
  const coarsePointer = useCoarsePointer();
  useAppViewport(narrowViewport || coarsePointer);
  const railPlacement = useRailPlacement();
  useMessageDeepLink();
  // Re-arm (or fire) a timed self-status persisted from a previous session.
  useEffect(() => initStatusTimer(), []);
  // Apply persisted appearance prefs (font size / density / reduced motion).
  useEffect(() => initAppearance(), []);
  const [mobileDrawerOpen, setMobileDrawerOpen] = useState(false);
  const [membersPanelOpen, setMembersPanelOpen] = useState(
    () => getUiPrefs().membersPanelOpen,
  );
  const [sidebarWidth, setSidebarWidth] = useState(() => getUiPrefs().sidebarWidth);
  const [membersWidth, setMembersWidth] = useState(() => getUiPrefs().membersWidth);
  // Mirror widths into refs so the drag-commit handler persists the latest
  // value without re-subscribing the handle on every pixel of movement.
  const sidebarWidthRef = useRef(sidebarWidth);
  sidebarWidthRef.current = sidebarWidth;
  const membersWidthRef = useRef(membersWidth);
  membersWidthRef.current = membersWidth;
  const [groupDmOpen, setGroupDmOpen] = useState(false);
  useEffect(() => onAppEvent('groupdm:create', () => setGroupDmOpen(true)), []);
  // Hamburger in the channel header toggles the mobile channel drawer.
  useEffect(() => onAppEvent('mobile:toggle-drawer', () => setMobileDrawerOpen((o) => !o)), []);
  // Members drawer on mobile uses its own ephemeral open state (default
  // closed) so the persisted desktop `membersPanelOpen` pref doesn't make the
  // panel auto-cover the chat every time you open a server on a phone.
  const [mobileMembersOpen, setMobileMembersOpen] = useState(false);
  useEffect(() => onAppEvent('mobile:toggle-members', () => setMobileMembersOpen((o) => !o)), []);
  // lg breakpoint: desktop shows the members panel inline, mobile as a drawer.
  const [isDesktop, setIsDesktop] = useState(() =>
    typeof window !== 'undefined' ? window.matchMedia('(min-width: 1024px)').matches : true,
  );
  useEffect(() => {
    const mq = window.matchMedia('(min-width: 1024px)');
    const on = () => {
      setIsDesktop(mq.matches);
      // Crossing up to desktop: the members panel renders inline, so drop any
      // leftover mobile drawer state (otherwise its backdrop scrim lingers and
      // dims the inline layout until the next click).
      if (mq.matches) setMobileMembersOpen(false);
    };
    mq.addEventListener('change', on);
    window.addEventListener('resize', on);
    on();
    return () => { mq.removeEventListener('change', on); window.removeEventListener('resize', on); };
  }, []);
  const verticalRail = railPlacement === 'left' && isDesktop;
  // md breakpoint: below it the left sidebar is hidden, so we use a mobile
  // master→detail flow — the main pane shows the channel/DM LIST when nothing
  // is selected, and the conversation (with a back arrow) once you pick one.
  const [isMobileNav, setIsMobileNav] = useState(() =>
    typeof window !== 'undefined' ? !window.matchMedia('(min-width: 768px)').matches : false,
  );
  useEffect(() => {
    const mq = window.matchMedia('(min-width: 768px)');
    const on = () => {
      setIsMobileNav(!mq.matches);
      // Crossing up to ≥md the left sidebar becomes inline again, so close the
      // mobile channel drawer — its backdrop scrim would otherwise stay up and
      // dim the desktop layout until dismissed by hand.
      if (mq.matches) setMobileDrawerOpen(false);
    };
    mq.addEventListener('change', on);
    window.addEventListener('resize', on);
    on();
    return () => { mq.removeEventListener('change', on); window.removeEventListener('resize', on); };
  }, []);

  const compactPlatform = embedded && isMobileNav;
  const [channelListOpen, setChannelListOpen] = useState(false);
  const lastGuild = useRef<string | null>(null);
  const channelByGuild = useRef<Record<string, string>>({});
  const currentChannelId = useDataStore((s) => s.currentChannelId);
  const currentGuildId = useDataStore((s) => s.currentGuildId);
  useEffect(() => {
    if (currentGuildId) {
      lastGuild.current = currentGuildId;
      if (currentChannelId) channelByGuild.current[currentGuildId] = currentChannelId;
    }
  }, [currentGuildId, currentChannelId]);
  const openGuildList = (id: string) => {
    const state = useDataStore.getState();
    state.selectGuild(id);
    const remembered = channelByGuild.current[id];
    if (remembered && state.guilds[id]?.channels.some(c => c.id === remembered)) state.selectChannel(remembered);
    setChannelListOpen(true);
  };
  // Phone, inside a server or an open DM: the top server rail is hidden and
  // shown at the top of the channel drawer instead (never both mounted).
  const railInDrawer = isRailInDrawer({ phone: isMobileNav, verticalRail, guildId: currentGuildId, channelId: currentChannelId });
  const [focusMode, setFocusMode] = useState(() =>
    isChannelFocusMode(useDataStore.getState().currentChannelId),
  );
  const [peekPanel, setPeekPanel] = useState<'left' | 'right' | null>(null);

  useEffect(() => {
    setFocusMode(isChannelFocusMode(currentChannelId));
    setPeekPanel(null);
  }, [currentChannelId]);

  useEffect(() => subscribeUiPrefs(() => setFocusMode(isChannelFocusMode(currentChannelId))), [currentChannelId]);

  const toggleFocusMode = useCallback(() => {
    if (!currentChannelId) return;
    setChannelFocusMode(currentChannelId, !focusMode);
    setPeekPanel(null);
  }, [currentChannelId, focusMode]);

  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.ctrlKey && event.shiftKey && event.key.toLowerCase() === 'f') {
        event.preventDefault();
        toggleFocusMode();
      }
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [toggleFocusMode]);
  useEffect(() => {
    setMobileDrawerOpen(false);
    setMobileMembersOpen(false);
  }, [currentChannelId]);

  useEffect(() => {
    return subscribeUiPrefs(() => setMembersPanelOpen(getUiPrefs().membersPanelOpen));
  }, []);

  useEffect(() => {
    const id = window.setInterval(() => {
      useDataStore.getState().pruneTyping();
    }, 2000);
    return () => window.clearInterval(id);
  }, []);

  useEffect(() => {
    if (!currentGuildId) {
      clearEmojis();
      return;
    }
    let cancelled = false;
    api
      .listEmojis(currentGuildId)
      .then((res) => {
        if (!cancelled) setGuildEmojis(res.emojis);
      })
      .catch(() => {
        if (!cancelled) clearEmojis();
      });
    return () => {
      cancelled = true;
    };
  }, [currentGuildId]);

  return (
    <div data-compact-platform={compactPlatform || undefined} className="app-shell relative flex h-full min-h-0 w-full min-w-0 flex-col overflow-hidden bg-background text-foreground">
      <VoiceAudioLayer />
      <QuickSwitcher />
      <MentionJumpHotkey />
      <SettingsHost />
      <WorkspacePanelsHost />
      <GroupDmModal open={groupDmOpen} onClose={() => setGroupDmOpen(false)} />
      <ForwardModal />
      <ReportModal />
      <RemindMenu />
      <RemindersModal />
      <DmCallModal />
      <BroadcastConsentModal />
      <BroadcastConsentBanner />
      <DmCallView />
      <MentionProfileHost />
      <ThreadPanel />
      <ImageLightbox />
      <ShortcutsOverlay />
      {/* Phone: inside a conversation the server rail gives its ~60px back to
          the chat; it moves into the channel drawer (below). */}
      {compactPlatform && <CompactNavigation channels={Boolean(currentGuildId)} onChats={() => { useDataStore.getState().goHome(); setChannelListOpen(false); }} onChannels={() => {
        const state = useDataStore.getState();
        const id = currentGuildId ?? (lastGuild.current && state.guilds[lastGuild.current] ? lastGuild.current : state.guildOrder[0]);
        if (id) openGuildList(id);
      }} onGuild={openGuildList}/>}
      {!compactPlatform && !verticalRail && !railInDrawer && <HeroTopBar compact={focusMode && isDesktop} />}
      {/* The rail + sidebar + gaps share ONE frame fill (bg-surface), so the
          left side has no internal seams; the chat and members float inside it
          as dark rounded panels (see below). The rail sits left (verticalRail)
          or on top per the placement setting. */}
      <div className="studio-workspace relative z-10 flex min-h-0 min-w-0 flex-1 bg-surface">
        {verticalRail && <HeroTopBar vertical compact={focusMode && isDesktop} />}
        {/* border-r: on light themes the sidebar fill matches the chat, so the
            two panels blended into one sheet — a hairline separator gives the
            seam back. The frameless palette zeroes --separator, so it is a no-op
            there. */}
        {focusMode && isDesktop ? (
          <div
            className="relative hidden w-12 shrink-0 items-start justify-center border-r border-separator bg-surface pt-3 md:flex"
            onMouseEnter={() => setPeekPanel('left')}
            onMouseLeave={() => setPeekPanel((panel) => panel === 'left' ? null : panel)}
          >
            <Tooltip label={t('header.exitFocus')} side="right">
              <button type="button" onClick={toggleFocusMode} className="grid size-8 place-items-center rounded-lg text-muted outline-none transition-colors hover:bg-default/60 hover:text-foreground focus-visible:ring-2 focus-visible:ring-accent" aria-label={t('header.exitFocus')}>
                <span aria-hidden>›</span>
              </button>
            </Tooltip>
            {peekPanel === 'left' && (
              <div className="absolute bottom-0 left-12 top-0 z-40 flex border-r border-separator bg-surface shadow-card" style={{ width: sidebarWidth }}>
                <HeroSidebar />
              </div>
            )}
          </div>
        ) : (
          <div className="studio-sidebar-frame hidden shrink-0 border-r border-separator md:flex" style={{ width: sidebarWidth }}>
            <HeroSidebar />
          </div>
        )}
        {!focusMode && <ResizeHandle
          ariaLabel={t('ui.heroApp.resizeChannels')}
          onResize={(dx) => setSidebarWidth((w) => clampSidebarWidth(w + dx))}
          onCommit={() =>
            updateUiPrefs({ sidebarWidth: clampSidebarWidth(sidebarWidthRef.current) })
          }
        />}

        {/* Mobile channel drawer — HeroUI Drawer (controlled). Opened by the
            hamburger in the channel header; only ever opened on mobile, so no
            need to gate by breakpoint. Gives focus-trap / scroll-lock / ESC /
            swipe-to-dismiss for free. */}
        <Drawer.Backdrop className="app-viewport-overlay" isOpen={mobileDrawerOpen} onOpenChange={setMobileDrawerOpen}>
          <Drawer.Content className="app-viewport-overlay app-mobile-drawer" placement="left">
            <Drawer.Dialog aria-label={t('ui.heroApp.channels')} className="app-mobile-drawer-dialog w-80 max-w-[90vw] p-0">
                <div className="flex h-full min-h-0 flex-col">
                  <div className="app-drawer-toolbar flex shrink-0 items-center">
                    <div className="min-w-0 flex-1">
                      {railInDrawer ? <HeroTopBar compact /> : <span className="px-4 text-sm font-medium">{t('ui.heroApp.channels')}</span>}
                    </div>
                    <button type="button" className="app-drawer-close" aria-label={t('common.close')} onClick={() => setMobileDrawerOpen(false)}>
                      <CloseIcon className="size-5" />
                    </button>
                  </div>
                  <div className="min-h-0 flex-1">
                    <HeroSidebar />
                  </div>
                </div>
            </Drawer.Dialog>
          </Drawer.Content>
        </Drawer.Backdrop>

        <main
          className={
            // The chat is a dark rounded panel floating in the frame. The gap
            // (m-2) shows the lighter frame all around, so the rounding reads
            // via the groove instead of a hairline seam.
            'flex min-w-0 flex-1 flex-col studio-room overflow-hidden bg-background'
          }
        >
          {!(compactPlatform && channelListOpen) && <HeroChannelHeader focusMode={focusMode && isDesktop} onToggleFocus={toggleFocusMode} />}
          <div className="min-h-0 flex-1">
            {/* Mobile DM master view ONLY: in personal messages (no guild),
                with no chat chosen the main pane is the DM list; picking a chat
                opens it full-screen with a back arrow. Server channels keep the
                classic hamburger→drawer flow (main pane is always the channel). */}
            {compactPlatform && channelListOpen && currentGuildId ? (
              <HeroSidebar onNavigate={() => setChannelListOpen(false)} />
            ) : isMobileNav && !currentGuildId && !currentChannelId ? (
              <HeroSidebar />
            ) : (
              <ContentPane />
            )}
          </div>
        </main>

        {/* Desktop: members panel inline (resizable). */}
        {isDesktop && membersPanelOpen && currentGuildId && !focusMode && (
          <>
            <ResizeHandle
              ariaLabel={t('ui.heroApp.resizeMembers')}
              onResize={(dx) => setMembersWidth((w) => clampMembersWidth(w - dx))}
              onCommit={() =>
                updateUiPrefs({ membersWidth: clampMembersWidth(membersWidthRef.current) })
              }
            />
            <HeroMemberPanel
              width={membersWidth}
              floating
              onCreateGroup={() => emitAppEvent('groupdm:create')}
            />
          </>
        )}
        {isDesktop && currentGuildId && focusMode && (
          <div
            className="relative flex w-12 shrink-0 items-start justify-center border-l border-separator bg-surface pt-3"
            onMouseEnter={() => setPeekPanel('right')}
            onMouseLeave={() => setPeekPanel((panel) => panel === 'right' ? null : panel)}
          >
            <Tooltip label={t('header.exitFocus')} side="left">
              <button type="button" onClick={toggleFocusMode} className="grid size-8 place-items-center rounded-lg text-muted outline-none transition-colors hover:bg-default/60 hover:text-foreground focus-visible:ring-2 focus-visible:ring-accent" aria-label={t('header.exitFocus')}>
                <span aria-hidden>‹</span>
              </button>
            </Tooltip>
            {peekPanel === 'right' && (
              <div className="absolute bottom-0 right-12 top-0 z-40 flex bg-surface shadow-card" style={{ width: membersWidth }}>
                <HeroMemberPanel width={membersWidth} onCreateGroup={() => emitAppEvent('groupdm:create')} />
              </div>
            )}
          </div>
        )}

        {/* Mobile/tablet: members panel as a right-side HeroUI Drawer so the
            members toggle still works on narrow screens (it would otherwise
            eat the viewport inline). */}
        {!isDesktop && currentGuildId && (
          <Drawer.Backdrop className="app-viewport-overlay" isOpen={mobileMembersOpen} onOpenChange={setMobileMembersOpen}>
            <Drawer.Content className="app-viewport-overlay app-mobile-drawer" placement="right">
              <Drawer.Dialog aria-label={t('ui.heroApp.members')} className="app-mobile-drawer-dialog w-80 max-w-[90vw] p-0">
                <HeroMemberPanel
                  width={288}
                  onCreateGroup={() => emitAppEvent('groupdm:create')}
                />
              </Drawer.Dialog>
            </Drawer.Content>
          </Drawer.Backdrop>
        )}
      </div>
    </div>
  );
}
